// ============================================================================
// AdminTagsPanel — manage event tags (Task #5).
// ============================================================================
// Sidebar entry: #admin/tags. UIF only.
//
// Tags appear in two categories: topic (food safety, conservation, etc.) and
// program (AMP, LFPP, etc.). Each row shows usage count; archiving a tag
// already linked to events does NOT remove the links — those events keep
// their tag, but the tag stops appearing in new pickers and public filters.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Save, X, Edit2, Archive, RotateCcw, AlertCircle, Tag as TagIcon } from 'lucide-react';
import { api, type AdminTag, type TagInput, type TagCategory } from '../api';

interface Props {
  token: string;
}

type FormMode =
  | { kind: 'closed' }
  | { kind: 'new' }
  | { kind: 'edit'; tag: AdminTag };

const emptyForm: TagInput = {
  tag_name: '',
  tag_category: 'topic',
  display_order: 100,
  description: null,
  is_active: true,
};

export function AdminTagsPanel({ token }: Props) {
  const [tags, setTags] = useState<AdminTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<FormMode>({ kind: 'closed' });
  const [form, setForm] = useState<TagInput>(emptyForm);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    api.adminTags(token)
      .then((d) => setTags(d.tags ?? []))
      .catch((err) => setError(err?.message || 'Could not load tags.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  function startNew() {
    setForm({ ...emptyForm });
    setMode({ kind: 'new' });
  }

  function startEdit(t: AdminTag) {
    setForm({
      tag_name: t.tag_name,
      tag_category: t.tag_category,
      display_order: t.display_order,
      description: t.description ?? null,
      is_active: t.is_active,
    });
    setMode({ kind: 'edit', tag: t });
  }

  function cancelForm() {
    setMode({ kind: 'closed' });
    setForm(emptyForm);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (!form.tag_name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (mode.kind === 'new') {
        await api.createTag(token, form);
      } else if (mode.kind === 'edit') {
        await api.updateTag(token, mode.tag.tag_id, form);
      }
      cancelForm();
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  async function archive(t: AdminTag) {
    const note = t.event_count > 0
      ? `Archive "${t.tag_name}"? It's currently linked to ${t.event_count} event(s); those links stay intact, the tag just stops appearing in new pickers and public filters.`
      : `Archive "${t.tag_name}"?`;
    if (!confirm(note)) return;
    try {
      await api.archiveTag(token, t.tag_id);
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not archive.');
    }
  }

  async function reactivate(t: AdminTag) {
    try {
      await api.updateTag(token, t.tag_id, { is_active: true });
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not re-activate.');
    }
  }

  const topicRows = tags.filter((t) => t.tag_category === 'topic');
  const programRows = tags.filter((t) => t.tag_category === 'program');

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tags</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Categorize events so the public filter row and admin search work. Topic tags (Food Safety, Conservation) describe content; program tags (AMP, LFPP) describe funding/affiliation.
          </p>
        </div>
        {mode.kind === 'closed' && (
          <button
            type="button"
            onClick={startNew}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
          >
            <Plus className="h-4 w-4" />
            Add tag
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {mode.kind !== 'closed' && (
        <form onSubmit={save} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-uif-green-dark">
              {mode.kind === 'new' ? 'New tag' : `Editing ${mode.tag.tag_name}`}
            </h2>
            <button
              type="button"
              onClick={cancelForm}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close form"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Name</span>
              <input
                type="text"
                required
                maxLength={50}
                value={form.tag_name}
                onChange={(e) => setForm({ ...form, tag_name: e.target.value })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Category</span>
              <select
                value={form.tag_category ?? 'topic'}
                onChange={(e) => setForm({ ...form, tag_category: e.target.value as TagCategory })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              >
                <option value="topic">Topic (content)</option>
                <option value="program">Program (funding/affiliation)</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Display order (lower = earlier)</span>
              <input
                type="number"
                min={0}
                value={form.display_order ?? 100}
                onChange={(e) => setForm({ ...form, display_order: e.target.value === '' ? 100 : Number(e.target.value) })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</span>
              <input
                type="text"
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value || null })}
                disabled={saving}
                placeholder="Shown as tooltip in the admin tag picker"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active ?? true}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                disabled={saving}
              />
              Active
            </label>
            <div className="flex-1" />
            <button
              type="button"
              onClick={cancelForm}
              disabled={saving}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.tag_name.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          </div>
        </form>
      )}

      <Section title="Topic tags" rows={topicRows} loading={loading} onEdit={startEdit} onArchive={archive} onReactivate={reactivate} />
      <Section title="Program tags" rows={programRows} loading={loading} onEdit={startEdit} onArchive={archive} onReactivate={reactivate} />
    </div>
  );
}

interface SectionProps {
  title: string;
  rows: AdminTag[];
  loading: boolean;
  onEdit: (t: AdminTag) => void;
  onArchive: (t: AdminTag) => void;
  onReactivate: (t: AdminTag) => void;
}

function Section({ title, rows, loading, onEdit, onArchive, onReactivate }: SectionProps) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">{title}</h2>
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {loading && (
          <div className="p-4 text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-4 text-sm text-gray-500 italic">No {title.toLowerCase()} yet.</div>
        )}
        {rows.map((t) => (
          <div key={t.tag_id} className="flex items-center gap-3 px-3 py-2">
            <TagIcon className={'h-4 w-4 flex-shrink-0 ' + (t.is_active ? 'text-uif-green-dark' : 'text-gray-300')} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={'text-sm font-medium ' + (t.is_active ? 'text-gray-900' : 'text-gray-400 line-through')}>
                  {t.tag_name}
                </span>
                {!t.is_active && <span className="text-[10px] uppercase tracking-wider text-gray-500">archived</span>}
                <span className="text-xs text-gray-500">· in use by {t.event_count} event{t.event_count === 1 ? '' : 's'}</span>
                <span className="text-xs text-gray-400">· order {t.display_order}</span>
              </div>
              {t.description && (
                <div className="text-xs text-gray-500 truncate">{t.description}</div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onEdit(t)}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                title="Edit"
                aria-label={`Edit ${t.tag_name}`}
              >
                <Edit2 className="h-4 w-4" />
              </button>
              {t.is_active ? (
                <button
                  type="button"
                  onClick={() => onArchive(t)}
                  className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                  title="Archive"
                  aria-label={`Archive ${t.tag_name}`}
                >
                  <Archive className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onReactivate(t)}
                  className="p-1.5 rounded hover:bg-gray-100 text-uif-green-dark"
                  title="Re-activate"
                  aria-label={`Re-activate ${t.tag_name}`}
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
